<script setup lang="ts">
import {
	ordenationSelectLabelVariant,
	type OrdenationDropdownOption,
	type OrdenationDropdownProps
} from '.';

withDefaults(defineProps<OrdenationDropdownProps>(), {
	labelVariant: 'default'
});

const emit = defineEmits<{
	(
		e: 'updateSelection',
		payload: {
			sortBy: OrdenationDropdownOption['sortBy'];
			orderBy: OrdenationDropdownOption['orderBy'];
		}
	): void;
	(e: 'reorder-clicked', value: OrdenationDropdownOption['value']): void;
}>();

const sortBy = defineModel<OrdenationDropdownOption['sortBy']>('sortBy');
const orderBy = defineModel<OrdenationDropdownOption['orderBy']>('orderBy');
const modelValue = ref<string | undefined>();
const label = ref<string | undefined>();

const options: OrdenationDropdownOption[] = [
	{
		label: 'Mais Relevantes',
		value: 'default_ordenation',
		sortBy: undefined,
		orderBy: undefined
	},
	{
		label: 'Menor preço',
		value: 'asking_price_asc',
		sortBy: 'asking_price',
		orderBy: 'asc'
	},
	{
		label: 'Maior preço',
		value: 'asking_price_desc',
		sortBy: 'asking_price',
		orderBy: 'desc'
	},
	{
		label: 'Menos fotos',
		value: 'images_count_asc',
		sortBy: 'images_count',
		orderBy: 'asc'
	},
	{
		label: 'Mais fotos',
		value: 'images_count_desc',
		sortBy: 'images_count',
		orderBy: 'desc'
	}
	// { label: 'Mais recentes', value: 'most_recent' },
	// { label: 'Mais antigos', value: 'older' },
	// { label: 'Exclusivos PilarHomes', value: 'pilar_exclusivities' },
	// { label: 'Menor valor por m²', value: 'square_meter_price_asc' },
	// { label: 'Maior valor por m²', value: 'square_meter_price_desc' }
];

function clearOrdenationSelect() {
	modelValue.value = undefined;
	label.value = undefined;
	sortBy.value = undefined;
	orderBy.value = undefined;
	emit('updateSelection', {
		sortBy: undefined,
		orderBy: undefined
	});

	emit('reorder-clicked', 'default_ordenation');
}

function updateModelValue(value: string) {
	if (value === 'default_ordenation') {
		clearOrdenationSelect();
		return;
	}

	modelValue.value = value;
	const option = options.find(option => option.value === value);

	if (!option) return;

	label.value = option?.label;
	sortBy.value = option.sortBy;
	orderBy.value = option.orderBy;

	emit('updateSelection', {
		sortBy: option.sortBy,
		orderBy: option.orderBy
	});

	emit('reorder-clicked', option.value);
}

watch(
	[sortBy, orderBy],
	([newSortBy, newOrderBy]) => {
		const option = options.find(
			option => option.sortBy === newSortBy && option.orderBy === newOrderBy
		);

		if (option && modelValue.value !== option.value) {
			modelValue.value = option.value;
			label.value = option.label;
		} else {
			modelValue.value = 'default_ordenation';
			label.value = options.find(
				option => option.value === 'default_ordenation'
			)?.label;
		}
	},
	{ immediate: true }
);
</script>

<template>
	<div class="relative flex flex-col items-end">
		<Select
			:model-value="modelValue"
			data-testid="select-ordenation"
			class="h-[50px] border border-white-dark-4 !p-0"
			@update:model-value="updateModelValue"
		>
			<SelectTrigger
				class="h-10 w-min border-none p-0 shadow-none focus:border-none focus:ring-0"
				arrow-class="hidden"
			>
				<Button
					variant="secondary"
					size="sm"
					:is-skeleton-loading="false"
					class="size-min px-3 focus:border-none md:px-4"
					:class="{
						'md:w-[170px]': labelVariant === 'default'
					}"
				>
					<IconMaterialSymbolsLightSwapVert class="size-5 text-primary" />
					<p :class="ordenationSelectLabelVariant({ variant: labelVariant })">
						{{ label }}
					</p>
				</Button>
			</SelectTrigger>

			<SelectContent
				class="absolute top-full mt-2 min-w-[200px] border border-white-dark-3 bg-white px-0 py-2 shadow-[0_1px_3px_1px_#00000026] md:min-w-[250px]"
				:class="{
					'translate-x-[-155px] md:translate-x-[-80px]':
						labelVariant === 'default',
					'translate-x-[-195px]': labelVariant !== 'default'
				}"
			>
				<SelectGroup class="!p-0">
					<SelectItem
						v-for="option in options"
						:key="`ordenation-select-option-${option.value}`"
						:value="option.value!"
						class="cursor-pointer whitespace-nowrap px-4 py-3 text-primary focus:bg-white-dark-1 focus:text-primary"
					>
						{{ option.label }}
					</SelectItem>
				</SelectGroup>
			</SelectContent>
		</Select>
	</div>
</template>
